import React from 'react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  BeakerIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  PaperAirplaneIcon,
  PlusIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import { statusNote } from '../utils/customReportUtils';

/** Shown before the first version exists. */
export const EmptyPanel = ({ synthesis, scope, loading, onAdd }) => (
  synthesis ? (
    <div className="custom-reports-editor-card custom-reports-blank">
      <strong>No in-depth report yet</strong>
      <span>
        Merges the finished session reports into one report: every speaker with their
        arguments, agreements and differences across sessions, and takeaways.
      </span>
      <button type="button" className="custom-reports-btn custom-reports-btn-primary" onClick={onAdd} disabled={loading}>
        <RectangleStackIcon /> New in-depth report
      </button>
    </div>
  ) : (
    <div className="custom-reports-editor-card custom-reports-blank">
      <strong>No report version yet</strong>
      <span>
        {scope === 'event'
          ? 'Create a version, paste your prompt, and pick the sessions it should run on.'
          : 'Create a version and paste the prompt it should run on this session.'}
      </span>
      <button type="button" className="custom-reports-btn custom-reports-btn-primary" onClick={onAdd} disabled={loading}>
        <PlusIcon /> New version
      </button>
    </div>
  )
);

export const PanelIntro = ({ title, synthesis, officialLabel }) => (
  <div className="custom-reports-intro">
    <strong>{title}</strong>
    {synthesis ? (
      <span>
        Merges the finished session reports of this event into one report: speakers in
        alphabetical order with their arguments, cross-session agreements and differences,
        and takeaways. Every claim keeps its citations back to the session transcript and
        sources. Leave the sessions empty to use every session with a completed report.
      </span>
    ) : (
      <span>
        This version uses only the prompt you paste. {officialLabel} stays unchanged.
        Name it in the field below — that name is what you see in the dropdown.
      </span>
    )}
  </div>
);

/** Header of the name/prompt section; when collapsed it summarises what is hidden. */
export const EditorToggle = ({ open, onToggle, title, collapsedHint }) => (
  <button
    type="button"
    className="custom-reports-collapse"
    onClick={onToggle}
    aria-expanded={open}
  >
    {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
    <span className="custom-reports-collapse-title">{title}</span>
    <span className="custom-reports-collapse-hint">{open ? 'Hide' : collapsedHint}</span>
  </button>
);

export const VersionNameField = React.forwardRef(function VersionNameField({ scopeId, value, onChange, onBlur, disabled }, ref) {
  return (
    <label className="custom-reports-field" htmlFor={`custom-report-title-${scopeId}`}>
      <span>Version name</span>
      <input
        id={`custom-report-title-${scopeId}`}
        ref={ref}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="custom-reports-control"
        disabled={disabled}
        placeholder="e.g. Djibouti briefing"
        onBlur={onBlur}
      />
    </label>
  );
});

export const GeneratingNote = ({ page }) => (
  <p className="custom-reports-note custom-reports-status">
    <ArrowPathIcon className="custom-reports-spin" />
    {statusNote(page) || 'Generating...'}
  </p>
);

const BusyIcon = ({ busy, Icon }) => (busy ? <ArrowPathIcon className="custom-reports-spin" /> : <Icon />);

/** Save, downloads, DigWatch / newsletter publishing and Generate. */
export const ReportActions = ({
  showOutputActions,
  eventPage,
  canMakeOfficial,
  running,
  saving,
  downloading,
  settingOfficial,
  publishingDigwatch,
  publishingMissions,
  sendingNewsletter,
  generating,
  canGenerate,
  generateLabel,
  languageLabel,
  onSave,
  onDownload,
  onMakeOfficial,
  onPublishDigwatch,
  onOpenMissions,
  showMissions,
  onCreateNewsletter,
}) => (
  <div className="custom-reports-actions">
    <button type="button" className="custom-reports-btn" onClick={onSave} disabled={running || saving}>
      {saving ? 'Saving...' : 'Save'}
    </button>
    {showOutputActions && (
      <>
        <button type="button" className="custom-reports-btn" onClick={() => onDownload('json')} disabled={!!downloading}>
          <BusyIcon busy={downloading === 'json'} Icon={ArrowDownTrayIcon} />
          Download JSON
        </button>
        <button type="button" className="custom-reports-btn" onClick={() => onDownload('pdf')} disabled={!!downloading}>
          <BusyIcon busy={downloading === 'pdf'} Icon={ArrowDownTrayIcon} />
          Download PDF
        </button>
        {eventPage && (
          <>
            {canMakeOfficial && (
              <button type="button" className="custom-reports-btn" onClick={onMakeOfficial} disabled={settingOfficial || running}>
                {settingOfficial ? 'Updating...' : 'Make official'}
              </button>
            )}
            <button
              type="button"
              className="custom-reports-btn"
              onClick={onPublishDigwatch}
              disabled={publishingDigwatch}
              title="Publish this report as a separate DigWatch page. The event page should link to it."
            >
              <BusyIcon busy={publishingDigwatch} Icon={GlobeAltIcon} />
              {publishingDigwatch ? 'Publishing...' : 'Publish to DigWatch'}
            </button>
          </>
        )}
        {showMissions && (
          <button
            type="button"
            className="custom-reports-btn"
            onClick={onOpenMissions}
            disabled={publishingMissions}
            title="Check the missions fields, then send this report."
          >
            <BusyIcon busy={publishingMissions} Icon={PaperAirplaneIcon} />
            {publishingMissions ? 'Sending...' : 'Send to missions'}
          </button>
        )}
        <button
          type="button"
          className="custom-reports-btn"
          onClick={onCreateNewsletter}
          disabled={sendingNewsletter}
          title={`Send the ${languageLabel} version to dig.watch as a newsletter draft`}
        >
          <BusyIcon busy={sendingNewsletter} Icon={EnvelopeIcon} />
          {sendingNewsletter ? 'Creating...' : 'Create newsletter'}
        </button>
      </>
    )}
    <button
      type="submit"
      className="custom-reports-btn custom-reports-btn-primary"
      disabled={generating || !canGenerate}
    >
      {generating ? (
        <><ArrowPathIcon className="custom-reports-spin" /> Generating...</>
      ) : (
        <><BeakerIcon /> {generateLabel}</>
      )}
    </button>
  </div>
);

/** Publish an in-depth report as the event's official DigWatch page, with when it last went out. */
export const SynthesisPublishBar = ({ publishing, digwatch, onPublish }) => (
  <div className="event-synthesis-publish">
    <button
      type="button"
      className="custom-reports-btn"
      onClick={onPublish}
      disabled={publishing}
      title="Publish as the event's official in-depth report on dig.watch. The event page links to it."
    >
      <BusyIcon busy={publishing} Icon={GlobeAltIcon} />
      {publishing ? 'Publishing...' : 'Publish in-depth report to DigWatch'}
    </button>
    {digwatch?.published_at && (
      <span className="custom-reports-note">
        Last published {new Date(`${digwatch.published_at}Z`).toLocaleString()}
        {digwatch.post_id ? ` (page ${digwatch.post_id})` : ''}
      </span>
    )}
  </div>
);
